import { Product, CartItem, SelectedVariation } from '@/types';
import { useStoreInfo } from '@/lib/store';

export interface DiscountInfo {
  hasDiscount: boolean;
  discountType: 'fixed' | 'percentage';
  discountAmount: number;
  threshold: number;
  currentQuantity: number;
  isEligible: boolean;
  discountValue: number;
  originalPrice: number;
  finalPrice: number;
}

export function calculateProductDiscount(product: Product, quantity: number = 1): DiscountInfo {
  const discountOff = parseFloat(product.discount_off) || 0;
  const discountQty = parseInt(product.discount_qty) || 0;
  const sellingPrice = parseFloat(product.sellingprice);
  
  const hasDiscount = discountOff > 0 && discountQty > 0;
  
  if (!hasDiscount) {
    return {
      hasDiscount: false,
      discountType: 'fixed',
      discountAmount: 0,
      threshold: 0,
      currentQuantity: quantity,
      isEligible: false,
      discountValue: 0,
      originalPrice: sellingPrice,
      finalPrice: sellingPrice
    };
  }

  const isEligible = quantity >= discountQty;
  let discountValue = 0;
  let finalPrice = sellingPrice;

  if (isEligible) {
    if (product.discount_type === 'percentage') {
      discountValue = (sellingPrice * discountOff) / 100;
      finalPrice = sellingPrice - discountValue;
    } else {
      discountValue = discountOff;
      finalPrice = Math.max(0, sellingPrice - discountOff);
    }
  }

  return {
    hasDiscount: true,
    discountType: product.discount_type as 'fixed' | 'percentage',
    discountAmount: discountOff,
    threshold: discountQty,
    currentQuantity: quantity,
    isEligible,
    discountValue,
    originalPrice: sellingPrice,
    finalPrice
  };
}

export function calculateVariationDiscount(
  product: Product, 
  variation: SelectedVariation, 
  allVariations: SelectedVariation[]
): DiscountInfo {
  const variationData = product.variations.find(v => v.variation === variation.variation);
  const variationPrice = variationData ? parseFloat(variationData.variation_amount) : 0;
  
  // Calculate total quantity across all variations for this product
  const totalQuantity = allVariations.reduce((sum, v) => sum + v.quantity, 0);
  
  const discountOff = parseFloat(product.discount_off) || 0;
  const discountQty = parseInt(product.discount_qty) || 0;
  
  const hasDiscount = discountOff > 0 && discountQty > 0;
  
  if (!hasDiscount) {
    return {
      hasDiscount: false,
      discountType: 'fixed',
      discountAmount: 0,
      threshold: 0,
      currentQuantity: totalQuantity,
      isEligible: false,
      discountValue: 0,
      originalPrice: variationPrice,
      finalPrice: variationPrice
    };
  }

  const isEligible = totalQuantity >= discountQty;
  let discountValue = 0;
  let finalPrice = variationPrice;

  if (isEligible) {
    if (product.discount_type === 'percentage') {
      discountValue = (variationPrice * discountOff) / 100;
      finalPrice = variationPrice - discountValue;
    } else {
      discountValue = discountOff;
      finalPrice = Math.max(0, variationPrice - discountOff);
    }
  }

  return {
    hasDiscount: true,
    discountType: product.discount_type as 'fixed' | 'percentage',
    discountAmount: discountOff,
    threshold: discountQty,
    currentQuantity: totalQuantity,
    isEligible,
    discountValue,
    originalPrice: variationPrice,
    finalPrice
  };
}

export function calculateCartItemDiscount(cartItem: CartItem): {
  totalOriginalPrice: number;
  totalDiscountValue: number;
  totalFinalPrice: number;
  discountInfo: DiscountInfo;
} {
  if (cartItem.selectedVariations && cartItem.selectedVariations.length > 0) {
    let totalOriginalPrice = 0;
    let totalDiscountValue = 0;
    let totalFinalPrice = 0;

    // Calculate discount for each variation
    cartItem.selectedVariations.forEach(variation => {
      const discountInfo = calculateVariationDiscount(cartItem, variation, cartItem.selectedVariations!);
      const variationTotal = discountInfo.originalPrice * variation.quantity;
      const variationDiscountTotal = discountInfo.discountValue * variation.quantity;
      const variationFinalTotal = discountInfo.finalPrice * variation.quantity;

      totalOriginalPrice += variationTotal;
      totalDiscountValue += variationDiscountTotal;
      totalFinalPrice += variationFinalTotal;
    });

    // Get discount info from first variation (they all share the same product discount rules)
    const firstVariation = cartItem.selectedVariations[0];
    const discountInfo = calculateVariationDiscount(cartItem, firstVariation, cartItem.selectedVariations);

    return {
      totalOriginalPrice,
      totalDiscountValue,
      totalFinalPrice,
      discountInfo
    };
  } else {
    const discountInfo = calculateProductDiscount(cartItem, cartItem.quantity);
    const totalOriginalPrice = discountInfo.originalPrice * cartItem.quantity;
    const totalDiscountValue = discountInfo.discountValue * cartItem.quantity;
    const totalFinalPrice = discountInfo.finalPrice * cartItem.quantity;

    return {
      totalOriginalPrice,
      totalDiscountValue,
      totalFinalPrice,
      discountInfo
    };
  }
}

export function formatDiscountDisplay(discountType: 'fixed' | 'percentage', discountAmount: number): string {
  if (discountType === 'percentage') {
    return `${discountAmount}% OFF`;
  } else {
    // Use the same currency formatting as the store
    const { storeInfo } = useStoreInfo.getState();
    const currency = storeInfo?.currenCy || 'USD';
    
    try {
      return `${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(discountAmount)} OFF`;
    } catch (error) {
      console.error('Invalid currency code:', currency);
      return `$${discountAmount.toFixed(2)} OFF`;
    }
  }
}